import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import {
  isWebPushConfigured,
  subscribeWithExistingRegistration,
  unsubscribePush,
} from '@/services/webPush';

type Props = {
  tifosiCle?: string;
};

const SWITCH_TRACK_W = 48;
const SWITCH_THUMB = 22;
const SWITCH_PAD = 3;
const THUMB_ON_LEFT = SWITCH_TRACK_W - SWITCH_PAD - SWITCH_THUMB;

export function PushNotificationsCard({ tifosiCle }: Props) {
  const colors = useTheme();
  const swEnabled = import.meta.env.VITE_ENABLE_SW === 'true';
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('unsupported');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const refreshState = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      setPermission('unsupported');
      return;
    }
    setSupported(true);
    setPermission(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch {
      setSubscribed(false);
    }
  }, []);

  useEffect(() => {
    void refreshState();
  }, [refreshState]);

  if (!swEnabled || !isWebPushConfigured()) {
    return null;
  }

  const handleEnable = async () => {
    setMessage(null);
    setBusy(true);
    try {
      if (!supported) {
        setMessage('Navigateur incompatible (Push API).');
        return;
      }
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setMessage('Permission refusee : les notifications restent desactivees.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      await subscribeWithExistingRegistration(reg, tifosiCle);
      setSubscribed(true);
      setMessage('');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Erreur lors de l'activation.");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setMessage(null);
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      await unsubscribePush(reg);
      setSubscribed(false);
      setMessage('Notifications desactivées.');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Erreur lors de la desactivation.');
    } finally {
      setBusy(false);
    }
  };

  const switchDisabled = busy || permission === 'denied' || !supported;

  const onSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) void handleEnable();
    else void handleDisable();
  };

  return (
    <div
      style={{
        ...cardStyle,
        backgroundColor: colors.surface,
        borderColor: colors.border,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Icon name="notifications-outline" size={22} color={Colors.primary} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: colors.textSecondary }}>
          NOTIFICATIONS PUSH
        </span>
      </div>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 14px', lineHeight: 1.45 }}>
      Activez les notifications pour recevoir les alertes de matchs, observations et informations importantes en temps réel.
      </p>
      {!supported ? (
        <span style={{ fontSize: 13, color: colors.textSecondary }}>Non pris en charge sur cet appareil.</span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {permission === 'denied' && (
            <span style={{ fontSize: 12, color: '#ff6b6b' }}>
              Les notifications sont bloquees dans les reglages du navigateur.
            </span>
          )}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 14,
              cursor: switchDisabled ? 'default' : 'pointer',
              opacity: busy ? 0.72 : 1,
              userSelect: 'none',
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 600, color: colors.text, flex: 1 }}>Activer notification</span>
            <span
              style={{
                position: 'relative',
                width: SWITCH_TRACK_W,
                height: 28,
                borderRadius: 14,
                backgroundColor: subscribed ? Colors.primary : colors.border,
                flexShrink: 0,
                transition: 'background-color 0.2s ease',
              }}
            >
              <input
                id="tifosi-push-notifications-switch"
                type="checkbox"
                role="switch"
                aria-checked={subscribed}
                aria-busy={busy}
                checked={subscribed}
                disabled={switchDisabled}
                onChange={onSwitchChange}
                style={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  margin: 0,
                  cursor: switchDisabled ? 'not-allowed' : 'pointer',
                }}
              />
              <span
                aria-hidden
                style={{
                  pointerEvents: 'none',
                  position: 'absolute',
                  top: SWITCH_PAD,
                  left: subscribed ? THUMB_ON_LEFT : SWITCH_PAD,
                  width: SWITCH_THUMB,
                  height: SWITCH_THUMB,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.28)',
                  transition: 'left 0.2s ease',
                }}
              />
            </span>
          </label>
        </div>
      )}
      {message && (
        <p style={{ fontSize: 12, color: colors.text, margin: '10px 0 0' }}>{message}</p>
      )}
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  padding: 16,
  borderRadius: 16,
  border: '1px solid',
  marginBottom: 16,
  display: 'flex',
  flexDirection: 'column',
};
